// src/context/AuthContext.js
// Auth provider and hook for app-wide auth state.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, writeBatch, serverTimestamp, collection, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { AUTH_ENSURE_USER_DEBOUNCE_MS } from '../constants/timeouts';
import { isAdminUser, effectivePlanForUser } from '../constants/uploadLimits';
import { resolveEffectivePlan } from '../utils/planResolution';
import { resolveStudioTrialExpiredNotice } from '../utils/studioTrialExpiredNotice';
import { STUDIO_SUBCOLLECTION_PLACEHOLDER_ID } from '../constants/studioProjects';
import { WORKSPACE_SEATS_ENABLED } from '../constants/seatPricing';
import {
  membershipMirrorSettingsCollection,
  membershipDocsFromSettingsSnapshot,
} from '../utils/membershipMirrorFirestore';

function workspaceRoutingUidForUser(uid) {
  return uid || null;
}
const CACHE_DISPLAY_NAME_KEY = 'starling_cached_displayName';
const CACHE_UID_KEY = 'starling_cached_uid';
/** { uid, plan, admin? } — avoids showing free-tier usage hint before billing doc loads */
const CACHE_BILLING_PLAN_KEY = 'starling_cached_billing_plan';

function readCachedBillingForUid(uid) {
  if (!uid) return { plan: null, admin: false };
  try {
    const raw = localStorage.getItem(CACHE_BILLING_PLAN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.uid !== uid) return { plan: null, admin: false };
    const plan = typeof parsed?.plan === 'string' && parsed.plan ? parsed.plan : null;
    const admin = parsed?.admin === true;
    return { plan: admin ? 'enterprise' : plan, admin };
  } catch (_) {}
  return { plan: null, admin: false };
}

/** Admin flag for viewer owner actions — matches top bar badges, including cached billing on first paint. */
export function resolveViewerIsAdmin(user, studioTopBarUser, authUid = null) {
  if (isAdminUser(user) || isAdminUser(studioTopBarUser)) return true;
  const uid = user?.uid || studioTopBarUser?.uid || authUid;
  if (!uid) return false;
  return readCachedBillingForUid(uid).admin === true;
}

function writeCachedPlan(uid, plan, admin = false) {
  if (!uid || !plan) return;
  try {
    localStorage.setItem(
      CACHE_BILLING_PLAN_KEY,
      JSON.stringify({
        uid,
        plan: admin ? 'enterprise' : plan,
        admin: admin === true,
      }),
    );
  } catch (_) {}
}

/** True when the user signed in with email/password (not Google-only, etc.). */
function isEmailPasswordUser(firebaseUser) {
  if (!firebaseUser?.providerData?.length) return false;
  return firebaseUser.providerData.some((p) => p?.providerId === 'password');
}

/**
 * Email/password: only Firestore `emailCodeVerified === false` means “new signup, OTP pending”.
 * Legacy password accounts omit the field (`undefined`) — treat as verified so existing users are not gated.
 * OAuth: `undefined` or missing field stays verified unless explicitly `false`.
 */
function resolveEmailCodeVerified(firebaseUser, userData) {
  const emailPassword = isEmailPasswordUser(firebaseUser);
  if (userData == null) {
    return emailPassword ? false : true;
  }
  if (emailPassword) {
    if (userData.emailCodeVerified === false) return false;
    return true;
  }
  return userData.emailCodeVerified !== false;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Full-screen logo + spinner during explicit sign-in / sign-out (not initial session check). */
  const [authSessionOverlay, setAuthSessionOverlay] = useState(false);
  /** Sync session from Firebase — updated at the start of `onAuthStateChanged` before token refresh (React `user` lags behind). */
  const [firebaseAuthUser, setFirebaseAuthUser] = useState(() => auth.currentUser);
  const ensureUserDebounceRef = useRef(null);
  const [workspaceMembershipOwnerIds, setWorkspaceMembershipOwnerIds] = useState([]);

  useEffect(() => {
    const uid = firebaseAuthUser?.uid;
    const onShopify = typeof window !== 'undefined'
      && window.location.pathname.startsWith('/shopify');
    if (!uid || !WORKSPACE_SEATS_ENABLED || onShopify) {
      setWorkspaceMembershipOwnerIds([]);
      return undefined;
    }
    const ownersFromSettingsMembership = new Set();
    const ownersFromLegacyMembership = new Set();

    const publish = () => {
      setWorkspaceMembershipOwnerIds(
        Array.from(new Set([
          ...ownersFromSettingsMembership,
          ...ownersFromLegacyMembership,
        ])),
      );
    };

    const unsubMemSettings = onSnapshot(
      membershipMirrorSettingsCollection(db, uid),
      (snap) => {
        ownersFromSettingsMembership.clear();
        for (const d of membershipDocsFromSettingsSnapshot(snap)) {
          const ownerUid = d.data()?.ownerUid || d.id;
          if (ownerUid && ownerUid !== uid) ownersFromSettingsMembership.add(ownerUid);
        }
        publish();
      },
      () => {
        ownersFromSettingsMembership.clear();
        publish();
      },
    );

    const unsubMemLegacy = onSnapshot(
      collection(db, 'users', uid, 'memberships'),
      (snap) => {
        ownersFromLegacyMembership.clear();
        for (const d of snap.docs) {
          if (d.id && d.id !== uid) ownersFromLegacyMembership.add(d.id);
        }
        publish();
      },
      () => {
        ownersFromLegacyMembership.clear();
        publish();
      },
    );

    return () => {
      unsubMemSettings();
      unsubMemLegacy();
    };
  }, [firebaseAuthUser?.uid]);

  const [selfBillingSeat, setSelfBillingSeat] = useState(null);
  const [selfBillingPlan, setSelfBillingPlan] = useState(null);

  const userUid = user?.uid;
  const userAdminFlag = user?.admin;
  useEffect(() => {
    const uid = userUid;
    const userIsAdmin = isAdminUser({ admin: userAdminFlag });
    if (!uid) {
      setSelfBillingSeat(null);
      setSelfBillingPlan(null);
      return undefined;
    }
    const planRef = doc(db, 'users', uid, 'billing', 'plan');
    const unsub = onSnapshot(
      planRef,
      (snap) => {
        const data = snap.exists?.() ? snap.data() : null;
        const sub = data?.subscription ?? null;
        const resolved = resolveEffectivePlan(data);
        const billingPlan = userIsAdmin ? 'enterprise' : resolved.plan;
        const trialNotice = resolveStudioTrialExpiredNotice(data, { isAdmin: userIsAdmin });
        setSelfBillingPlan({
          plan: billingPlan,
          planSource: userIsAdmin ? 'subscription' : resolved.source,
          studioTrialExpired: trialNotice.active,
          studioTrialExpiredAt: trialNotice.expiredAt,
        });
        setSelfBillingSeat({
          seatDowngradePending: !!sub?.seatDowngradePending,
          seatCap: sub?.seatCap ?? null,
          seatAddonQuantity: sub?.seatAddonQuantity ?? 0,
        });
        if (userIsAdmin) {
          writeCachedPlan(uid, 'enterprise', true);
        } else {
          writeCachedPlan(uid, resolved.plan, false);
          setUser((prev) => {
            if (!prev || prev.uid !== uid) return prev;
            return {
              ...prev,
              plan: resolved.plan,
              planStartedAt:
                typeof sub?.planStartedAt === 'number' ? sub.planStartedAt : prev.planStartedAt,
            };
          });
        }
      },
      () => {
        setSelfBillingSeat(null);
        setSelfBillingPlan(null);
      },
    );
    return () => unsub();
  }, [userUid, userAdminFlag]);

  const markEmailCodeVerified = useCallback(() => {
    setUser((prev) => (prev ? { ...prev, emailCodeVerified: true } : prev));
  }, []);

  const refreshUserBillingPlan = useCallback(async () => {
    const authUser = auth.currentUser;
    if (!authUser) return;
    try {
      await authUser.getIdToken();
      const userDoc = await getDoc(doc(db, 'users', authUser.uid));
      const userData = userDoc.exists?.() ? userDoc.data() : null;
      let plan = 'free';
      let planStartedAt = null;
      if (isAdminUser(userData)) {
        plan = 'enterprise';
      } else {
        const billingDoc = await getDoc(doc(db, 'users', authUser.uid, 'billing', 'plan'));
        if (billingDoc.exists?.()) {
          const billingData = billingDoc.data();
          const sub = billingData?.subscription;
          plan = resolveEffectivePlan(billingData).plan;
          if (typeof sub?.planStartedAt === 'number') planStartedAt = sub.planStartedAt;
        }
      }
      const emailCodeVerified = resolveEmailCodeVerified(authUser, userData);
      const admin = isAdminUser(userData) ?? false;
      setUser((prev) => {
        if (!prev || prev.uid !== authUser.uid) return prev;
        return {
          ...prev,
          plan,
          planStartedAt,
          emailCodeVerified,
          admin,
        };
      });
      writeCachedPlan(authUser.uid, plan, admin);
    } catch (_) {}
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setFirebaseAuthUser(firebaseUser ?? null);
      if (firebaseUser) {
        // Await fresh token before exposing user + clearing loading so Firestore listeners attach
        // with credentials (avoids permission-denied on localhost memory cache cold load).
        void (async () => {
          try {
            await firebaseUser.getIdToken(true);
          } catch (_) {}
          const cachedBilling = readCachedBillingForUid(firebaseUser.uid);
          let emailCodeVerified = resolveEmailCodeVerified(firebaseUser, null);
          let initialPlan = cachedBilling.plan || 'free';
          let initialAdmin = cachedBilling.admin === true;
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (userDoc.exists?.()) {
              const userData = userDoc.data();
              emailCodeVerified = resolveEmailCodeVerified(firebaseUser, userData);
              if (isAdminUser(userData)) {
                initialAdmin = true;
                initialPlan = 'enterprise';
              }
            }
          } catch (_) {}
          // Use last known billing plan so captures hint doesn't flash "5 free" for Pro users
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || firebaseUser.email || 'Account',
            plan: initialPlan,
            emailCodeVerified,
            admin: initialAdmin,
          });
          setLoading(false);
          // Keep authSessionOverlay until the post-login route paints (cleared in App when leaving /login).
          // Resolve true plan immediately (don't wait for ensureUser debounce)
          void refreshUserBillingPlan().catch(() => {});

          if (ensureUserDebounceRef.current) clearTimeout(ensureUserDebounceRef.current);
          const userToEnsure = firebaseUser;
          ensureUserDebounceRef.current = setTimeout(async () => {
            ensureUserDebounceRef.current = null;
            try {
              const ensureUserDocument = httpsCallable(functions, 'ensureUserDocument');
              await ensureUserDocument();
            } catch (err) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[Auth] ensureUserDocument failed:', err?.message || err);
              }
            }
            // Fallback: create non-billing subcollections from client if missing (billing is server-only).
            const uid = userToEnsure.uid;
            try {
              await userToEnsure.getIdToken(true);
              const batch = writeBatch(db);
              const userRef = doc(db, 'users', uid);
              const prefsRef = doc(userRef, 'settings', 'preferences');
              const studioRef = doc(userRef, 'studio', STUDIO_SUBCOLLECTION_PLACEHOLDER_ID);
              const [prefsSnap, studioSnap] = await Promise.all([getDoc(prefsRef), getDoc(studioRef)]);
              if (!prefsSnap.exists() || !studioSnap.exists()) {
                const now = serverTimestamp();
                const dn = userToEnsure.displayName?.trim?.() || '';
                if (!prefsSnap.exists() && !studioSnap.exists()) {
                  batch.set(
                    userRef,
                    {
                      uid,
                      email: userToEnsure.email ?? '',
                      ...(dn ? {displayName: dn} : {}),
                      createdAt: now,
                      updatedAt: now,
                    },
                    {merge: true}
                  );
                }
                if (!prefsSnap.exists()) {
                  batch.set(prefsRef, {
                    userId: uid,
                    notificationsOn: true,
                    signOutEverywhereClickCount: 0,
                    deleteAccountAttemptCount: 0,
                    createdAt: now,
                    updatedAt: now,
                  }, {merge: true});
                }
                if (!studioSnap.exists()) {
                  batch.set(studioRef, {
                    _placeholder: true,
                    projectName: '',
                    authorizedUsers: [{ uid, role: 'owner' }],
                    memberIds: [uid],
                    captureCount: 0,
                    createdAt: now,
                    updatedAt: now,
                  }, {merge: true});
                }
                try {
                  await batch.commit();
                } catch (e) {
                  const msg = e?.message || String(e);
                  console.error('[Auth] client bootstrap failed:', msg);
                }
              }
            } catch (e) {
              if (process.env.NODE_ENV === 'development') {
                console.warn('[Auth] client subcollection bootstrap failed:', e?.message || e);
              }
            }
            // Use displayName from Firestore (source of truth), fallback to Auth then email
            let displayName = userToEnsure.displayName || userToEnsure.email || 'Account';
            let userData = null;
            try {
              const userDoc = await getDoc(doc(db, 'users', userToEnsure.uid));
              userData = userDoc.exists?.() ? userDoc.data() : null;
              const firestoreName = userData?.displayName;
              const trimmed = firestoreName && String(firestoreName).trim();
              if (trimmed) {
                displayName = trimmed;
              }
            } catch (_) {}
            let plan = 'free';
            let planStartedAt = null;
            if (isAdminUser(userData)) {
              plan = 'enterprise';
            } else {
              try {
                const billingDoc = await getDoc(doc(db, 'users', userToEnsure.uid, 'billing', 'plan'));
                if (billingDoc.exists?.()) {
                  const billingData = billingDoc.data();
                  const sub = billingData?.subscription;
                  plan = resolveEffectivePlan(billingData).plan;
                  if (typeof sub?.planStartedAt === 'number') planStartedAt = sub.planStartedAt;
                }
              } catch (_) {}
            }
            const admin = isAdminUser(userData) ?? false;
            setUser({
              uid: userToEnsure.uid,
              email: userToEnsure.email,
              displayName,
              plan,
              planStartedAt,
              emailCodeVerified: resolveEmailCodeVerified(userToEnsure, userData),
              admin,
            });
            writeCachedPlan(userToEnsure.uid, plan, admin);
            try {
              localStorage.setItem(CACHE_DISPLAY_NAME_KEY, displayName);
              localStorage.setItem(CACHE_UID_KEY, userToEnsure.uid);
            } catch (_) {}
          }, AUTH_ENSURE_USER_DEBOUNCE_MS);
        })();
      } else {
        if (ensureUserDebounceRef.current) {
          clearTimeout(ensureUserDebounceRef.current);
          ensureUserDebounceRef.current = null;
        }
        setUser(null);
        try {
          localStorage.removeItem(CACHE_DISPLAY_NAME_KEY);
          localStorage.removeItem(CACHE_UID_KEY);
          localStorage.removeItem(CACHE_BILLING_PLAN_KEY);
        } catch (_) {}
        setLoading(false);
        setAuthSessionOverlay(false);
      }
    });
    return () => {
      if (ensureUserDebounceRef.current) clearTimeout(ensureUserDebounceRef.current);
      unsubscribe();
    };
  }, [refreshUserBillingPlan]);

  useEffect(() => {
    const refreshOnEvent = () => { refreshUserBillingPlan().catch(() => {}); };
    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') refreshOnEvent();
    };
    window.addEventListener('starling:refresh-user-plan', refreshOnEvent);
    document.addEventListener('visibilitychange', refreshOnVisible);
    return () => {
      window.removeEventListener('starling:refresh-user-plan', refreshOnEvent);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [refreshUserBillingPlan]);

  const enrichedUser = useMemo(() => {
    if (!user) return null;
    const workspaceRoutingUid = workspaceRoutingUidForUser(user.uid);
    const cachedBilling = readCachedBillingForUid(user.uid);
    const admin = isAdminUser(user) || cachedBilling.admin === true;
    let merged = {
      ...user,
      admin,
      workspaceRoutingUid,
      workspaceMembershipOwnerIds: workspaceMembershipOwnerIds,
    };
    merged.plan = admin
      ? 'enterprise'
      : (selfBillingPlan?.plan ?? effectivePlanForUser(merged));
    if (!admin && selfBillingPlan?.planSource) {
      merged = {
        ...merged,
        planSource: selfBillingPlan.planSource,
      };
    }
    if (selfBillingPlan) {
      merged = {
        ...merged,
        studioTrialExpired: !!selfBillingPlan.studioTrialExpired,
        studioTrialExpiredAt: selfBillingPlan.studioTrialExpiredAt ?? null,
      };
    }
    if (selfBillingSeat) {
      merged = {
        ...merged,
        workspaceSeatCap: selfBillingSeat.seatCap ?? merged.workspaceSeatCap,
        workspaceSeatDowngradePending: !!selfBillingSeat.seatDowngradePending,
        workspaceSeatAddonQuantity: selfBillingSeat.seatAddonQuantity ?? 0,
      };
    }
    return merged;
  }, [user, workspaceMembershipOwnerIds, selfBillingSeat, selfBillingPlan]);

  const studioTopBarUser = useMemo(() => {
    if (enrichedUser) return enrichedUser;
    const fu = firebaseAuthUser;
    if (!fu) return null;
    const cachedBilling = readCachedBillingForUid(fu.uid);
    const pseudo = {
      uid: fu.uid,
      email: fu.email,
      displayName: fu.displayName || fu.email || 'Account',
      plan: cachedBilling.plan || 'free',
      emailCodeVerified: resolveEmailCodeVerified(fu, null),
      admin: cachedBilling.admin === true,
    };
    return {
      ...pseudo,
      workspaceRoutingUid: workspaceRoutingUidForUser(pseudo.uid),
      workspaceMembershipOwnerIds: workspaceMembershipOwnerIds,
    };
  }, [enrichedUser, firebaseAuthUser, workspaceMembershipOwnerIds]);

  const value = useMemo(() => ({
    user: enrichedUser,
    loading,
    authSessionOverlay,
    setAuthSessionOverlay,
    firebaseAuthUser,
    studioTopBarUser,
    refreshUserBillingPlan,
    markEmailCodeVerified,
  }), [
    enrichedUser,
    loading,
    authSessionOverlay,
    firebaseAuthUser,
    studioTopBarUser,
    refreshUserBillingPlan,
    markEmailCodeVerified,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
